import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Layout from '../../components/Layout/Layout';
import useConfirmationModal from '../../hooks/useConfirmationModal';
import { ICoupon } from '../../interfaces/coupon';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import { showToastNotification } from '../../utility/helper';
import InputField from '../../components/UI/Forms/InputField';
import ErrorMessage from '../../components/UI/Notifications/ErrorMessage';
import Button from '../../components/UI/Buttons/Button';
import ConfirmationModal from '../../components/UI/Modal/ConfirmationModal';
import moment from '../../utility/moment-timezone';
import Table from '../../components/UI/CommonTable/CommonTable';

const Coupons: React.FC = () => {
    const [coupons, setCoupons] = useState<ICoupon[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({ mode: 'onBlur' });
    const [loadingButton, setLoadingButton] = useState<boolean>(false);
    const [editingCoupon, setEditingCoupon] = useState<ICoupon | null>(null);
    const { showModal, modalProps, confirmAction } = useConfirmationModal();

    const onSubmit = async (data: any) => {
        setLoadingButton(true);

        try {
            if (editingCoupon) {
                await callApi(API_URL_2 + API_ENDPOINT.order + `/coupons/${editingCoupon.id}`, { method: 'PUT', data });
                showToastNotification('success', 'Coupon updated successfully');
            } else {
                await callApi(API_URL_2 + API_ENDPOINT.order + '/coupons', { method: 'POST', data });
                showToastNotification('success', 'Coupon added successfully');
            }

            reset();
            setEditingCoupon(null);
            getCoupons();
        } catch (error: any) {
            showToastNotification('danger', error.message);
        } finally {
            setLoadingButton(false);
        }
    };

    useEffect(() => {
        getCoupons();
    }, []);

    const getCoupons = async () => {
        try {
            setLoading(true);
            const response = await callApi(API_URL_2 + API_ENDPOINT.order + '/coupons', { method: 'GET' });
            setCoupons(response);
        } catch (error: any) {
            console.error('Failed to fetch coupons:', error.message);
        } finally {
            setLoading(false);
        }
    };

    const deleteCoupon = async (id: string) => {
        try {
            await callApi(API_URL_2 + API_ENDPOINT.order + `/coupons/${id}`, { method: 'DELETE' });
            setCoupons((prevCoupons) => prevCoupons.filter((coupon) => coupon.id !== id));
        } catch (error: any) {
            console.error('Failed to delete coupon:', error.message);
        }
    };

    const handleEditCoupon = (coupon: ICoupon) => {
        setEditingCoupon(coupon);
        setValue('code', coupon.code);
        setValue('description', coupon.description);
        setValue('valueType', coupon.type);
        setValue('discountValue', coupon.discountValue);
        setValue('maxDiscount', coupon.maxDiscount);
        setValue('startDate', moment(coupon.startDate).format("YYYY-MM-DD"));
        setValue('expirationDate', moment(coupon.expirationDate).format("YYYY-MM-DD"));
        setValue('minimumValue', coupon.minimumValue);
    };

    const handleDeleteClick = (id: string) => {
        confirmAction({
            title: 'Confirm Delete',
            message: 'Are you sure you want to delete this coupon?',
            onConfirm: () => deleteCoupon(id),
        });
    };

    const columns = [
        { header: "Code", accessor: "code" },
        { header: "Description", accessor: "description" },
        {
            header: "Value Type",
            accessor: "type",
            render: (type: string) => (type === 'percentage' ? 'Percentage' : 'Flat'),
        },
        { header: "Discount", accessor: "discountValue" },
        {
            header: "Start Date",
            accessor: "startDate",
            render: (startDate: string) => moment(startDate).format("DD MMM, YYYY"),
        },
        {
            header: "Expiration Date",
            accessor: "expirationDate",
            render: (expirationDate: string) => moment(expirationDate).format("DD MMM, YYYY"),
        },

        { header: "Minimum Value", accessor: "minimumValue" },
    ];

    const actions = [
        {
            icon: "bi bi-pen",
            color: "success",
            onClick: (coupon: ICoupon) => handleEditCoupon(coupon),
        },
        {
            icon: "bi bi-trash3",
            color: "danger",
            onClick: (coupon: ICoupon) => handleDeleteClick(coupon.id),
        },
    ];


    return (
        <Layout>
            <div className='d-flex justify-content-between align-items-start'>
                <h1 className="app-page-title">Coupons</h1>
            </div>
            <div className="row">
                <div className="col-lg-8">
                    <div className="app-card app-card-orders-table shadow-sm mb-4 p-4">
                        <div className="app-card-body">
                            <Table
                                columns={columns}
                                data={coupons}
                                loading={loading}
                                actions={actions}
                                emptyMessage="No Orders available."
                            />
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="app-card app-card-settings shadow-sm p-4">
                        <div className="app-card-body">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <div className="row">
                                    {FieldConfigration.map((field, index) => (
                                        <div className="mb-3 col-12" key={index}>
                                            {field.type === 'radio' ? (
                                                <div>
                                                    <label className="form-label d-block">{field.label}</label>
                                                    {field.options?.map((opt) => (
                                                        <div className="form-check form-check-inline" key={opt.value}>
                                                            <input
                                                                className="form-check-input"
                                                                type="radio"
                                                                id={`${field.name}-${opt.value}`}
                                                                value={opt.value}
                                                                {...register(field.name, field.validation)}
                                                            />
                                                            <label className="form-check-label" htmlFor={`${field.name}-${opt.value}`}>
                                                                {opt.label}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <InputField
                                                    type={field.type}
                                                    name={field.name}
                                                    label={field.label}
                                                    placeholder={field.placeholder}
                                                    register={register(field.name, field.validation)}
                                                />
                                            )}
                                            <ErrorMessage message={errors[field.name]?.message?.toString()} />
                                        </div>
                                    ))}
                                </div>
                                <div className="col-12 text-end mt-3">
                                    <Button
                                        loading={loadingButton}
                                        className="btn btn-primary text-white px-5"
                                        type="submit"
                                        text={editingCoupon ? 'Update' : 'Save'}
                                    />
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <ConfirmationModal {...modalProps} show={showModal} />
        </Layout>
    );
};

export default Coupons;

// const today = new Date().toISOString().split('T')[0]
const FieldConfigration = [
    {
        name: 'code',
        label: 'Code',
        type: 'text',
        placeholder: 'Enter coupon code',
        validation: { required: 'Code is required' },
    },
    {
        name: 'description',
        label: 'Description',
        type: 'text',
        placeholder: 'Enter description',
        validation: { required: 'Description is required' },
    },
    {
        name: 'type',
        label: 'Value Type',
        type: 'radio',
        options: [
            { label: 'Percentage', value: 'percentage' },
            { label: 'Flat', value: 'flat' },
        ],
        validation: { required: 'Value type is required' },
    },
    {
        name: 'discountValue',
        label: 'Discount Value',
        type: 'number',
        placeholder: 'Enter discount percentage',
        validation: {
            required: 'Discount percentage is required',
            validate: (value: number) => value > 0 || 'Discount percentage must be greater than 0',
        },
    },
    {
        name: 'maxDiscount',
        label: 'Max Discount',
        type: 'number',
        placeholder: 'Enter max discount',
        validation: {
            required: 'Max discount is required',
            validate: (value: number) => value > 0 || 'Max discount must be greater than 0',
        },
    },
    {
        name: 'startDate',
        label: 'Start Date',
        type: 'date',
        placeholder: 'Enter Start date',
        validation: {
            required: 'Start date is required',
        },
    },
    {
        name: 'expirationDate',
        label: 'Expiration Date',
        type: 'date',
        placeholder: 'Enter expiration date',
        validation: {
            required: 'Expiration date is required',
            validate: (value: string) => {
                const selectedDate = new Date(value);
                const currentDate = new Date();
                return selectedDate > currentDate || 'Expiration date must be a future date';
            },
        },
    },
    {
        name: 'minimumValue',
        label: 'Minimum Purchase Value',
        type: 'number',
        placeholder: 'Enter minimum value',
        validation: {
            required: 'Minimum purchase value is required',
            validate: (value: number) => value >= 0 || 'Minimum purchase value cannot be negative',
        },
    },
]
